import { useCallback, useMemo, useRef, useState } from 'react'
import { getApiErrorMessage } from '../../../shared/api/apiClient'
import { createSemaphore } from '../../../shared/utils/concurrency'
import { DOCUMENT_STATUS } from '../config/brandDetailsConfig'
import { documentUploadService as sharedUploadService } from '../services/documentUploadService'

// Whole files in flight at once. Each file spends its own chunk-level
// concurrency budget on top of this, so keep the two in proportion.
const MAX_CONCURRENT_FILE_UPLOADS = 2

// Progress past this point belongs to the Brand DNA save, not the transfer.
const SAVING_PROGRESS_THRESHOLD = 95

const createJobId = ({ documentType, file }) =>
  [documentType.id, file.name, file.size, file.lastModified].join('-')

const isInFlight = (status) =>
  status === DOCUMENT_STATUS.UPLOADING || status === DOCUMENT_STATUS.SAVING

// Smallest first. With only a couple of file slots, a 2 MB document finishes in
// seconds instead of waiting out every chunk of a 250 MB one queued beside it.
const bySizeAscending = (left, right) => left.size - right.size

export const useDocumentUploads = ({
  brand,
  onDocumentSaved,
  uploadService = sharedUploadService,
}) => {
  const [uploadJobs, setUploadJobs] = useState([])
  // Held in a ref so the cap spans every batch the user queues, and so a
  // render never hands the running jobs a fresh pool.
  const fileSemaphore = useRef(createSemaphore(MAX_CONCURRENT_FILE_UPLOADS))
  // The authoritative job records. Uploads run outside the React tree, so they
  // read the file, status, and resume state from here instead of from a
  // captured render's closure. `uploadJobs` is the render projection.
  const jobsById = useRef(new Map())

  const patchJob = useCallback((jobId, changes) => {
    const job = jobsById.current.get(jobId)
    if (!job) return
    Object.assign(job, changes)

    setUploadJobs((current) => {
      const index = current.findIndex((item) => item.id === jobId)
      if (index === -1) return current
      const hasChange = Object.entries(changes).some(
        ([key, value]) => current[index][key] !== value,
      )
      // Parallel chunks report progress constantly; dropping the no-op writes
      // keeps a 250 MB upload from re-rendering on every socket event.
      if (!hasChange) return current

      const next = [...current]
      next[index] = { ...current[index], ...changes }
      return next
    })
  }, [])

  const runJob = useCallback(
    async (jobId) => {
      const queued = jobsById.current.get(jobId)
      if (!queued) return
      const release = await fileSemaphore.current.acquire(queued.file.size)
      try {
        const job = jobsById.current.get(jobId)
        // Dismissed while it sat in the queue.
        if (!job) return

        patchJob(jobId, { error: '', status: DOCUMENT_STATUS.UPLOADING })

        await uploadService.uploadDocument({
          brand,
          documentType: job.documentType,
          enterpriseUpload: job.enterpriseUpload,
          file: job.file,
          onProgress: (progress) =>
            patchJob(jobId, {
              progress,
              status:
                progress >= SAVING_PROGRESS_THRESHOLD
                  ? DOCUMENT_STATUS.SAVING
                  : DOCUMENT_STATUS.UPLOADING,
            }),
          onSession: (uploadSession) =>
            patchJob(jobId, {
              completedChunks: uploadSession.partEtags.filter(Boolean).length,
              totalChunks: uploadSession.partEtags.length,
              uploadSession,
            }),
          uploadSession: job.uploadSession,
        })

        patchJob(jobId, { progress: 100, status: DOCUMENT_STATUS.UPLOADED })
        await onDocumentSaved?.()
      } catch (error) {
        patchJob(jobId, {
          // AssetHub finished but Brand DNA rejected the save: keeping the
          // asset lets the retry skip the transfer entirely.
          ...(error?.enterpriseUpload
            ? { enterpriseUpload: error.enterpriseUpload }
            : {}),
          error: getApiErrorMessage(
            error,
            error?.message || 'This document could not be uploaded.',
          ),
          status: DOCUMENT_STATUS.FAILED,
        })
      } finally {
        release()
      }
    },
    [brand, onDocumentSaved, patchJob, uploadService],
  )

  // Returns immediately. The caller clears its form and the session panel
  // reports the rest, so the user can queue another document right away.
  const queueUploads = useCallback(
    ({ documentType, files }) => {
      const smallestFirst = [...files].sort(bySizeAscending)

      smallestFirst.forEach((file) => {
        const jobId = createJobId({ documentType, file })
        const existing = jobsById.current.get(jobId)
        // Re-queueing a file that is already moving would upload it twice.
        if (existing && isInFlight(existing.status)) return

        if (existing) {
          existing.status = DOCUMENT_STATUS.UPLOADING
        } else {
          jobsById.current.set(jobId, {
            documentType,
            file,
            id: jobId,
            progress: 0,
            status: DOCUMENT_STATUS.UPLOADING,
          })
          setUploadJobs((current) => [
            ...current,
            {
              documentTypeLabel: documentType.label,
              fileName: file.name,
              fileSize: file.size,
              id: jobId,
              progress: 0,
              status: DOCUMENT_STATUS.UPLOADING,
            },
          ])
        }
        void runJob(jobId)
      })
    },
    [runJob],
  )

  const retryUpload = useCallback(
    (jobId) => {
      const job = jobsById.current.get(jobId)
      if (!job || isInFlight(job.status)) return
      job.status = DOCUMENT_STATUS.UPLOADING
      void runJob(jobId)
    },
    [runJob],
  )

  const dismissUpload = useCallback((jobId) => {
    const job = jobsById.current.get(jobId)
    if (job && isInFlight(job.status)) return
    jobsById.current.delete(jobId)
    setUploadJobs((current) => current.filter(({ id }) => id !== jobId))
  }, [])

  return useMemo(
    () => ({ dismissUpload, queueUploads, retryUpload, uploadJobs }),
    [dismissUpload, queueUploads, retryUpload, uploadJobs],
  )
}
